// What the phone page shows about its mic: it must be unmistakable whether the mic is listening.
// The one source of truth is the capture itself (`VoiceKitClient.micCapturing`: the page's mic is
// sending and audio is arriving), never the session's state: a session can run with the mic off
// (a typed turn's, the PC's own, after the mic stalled or the page was paused).

import type { OrbState } from "../orb";
import type { KitState } from "../protocol";

export interface MicViewInput {
  /** The kit's state. */
  kit: KitState;
  /** The page's mic is really capturing (`VoiceKitClient.micCapturing`). */
  micOn: boolean;
  /** The mic was stopped for the page (backgrounded, a call, a stall): the next tap resumes. */
  paused?: boolean;
  /** The reply being spoken is paused (a tap, a call; protocol 1.9): the next tap carries on. */
  replyPaused?: boolean;
  /**
   * The mic is on for the session but iOS muted its track (MicCapture.trackMuted): "muted", the
   * next tap fetches a fresh one; "reconnecting" while it is on its way; "other" when a fresh
   * one came back muted too (another app holds the mic: the next tap ends voice); "stuck" when
   * the reconnect hasn't answered in time (the next tap ends voice). Not a call: the reply keeps
   * playing. See ./mic-reconnect.ts.
   */
  held?: "muted" | "other" | "reconnecting" | "stuck" | null;
}

export interface MicView {
  /** The mic indicator: "Mic on" or "Mic off". */
  mic: "on" | "off";
  /** The orb: "listening" (and its motion) only while the mic is on; with the mic off it rests
   * ("idle", still) and the page shows it powered down (body[data-mic="off"]), whatever the kit
   * is doing, so its colour and motion never suggest the phone is listening. */
  orb: OrbState;
  /** The line under the orb. */
  label:
    | "Listening"
    | "Mic off · Tap to talk"
    | "Thinking"
    | "Speaking"
    | "Paused"
    | "Paused · tap to continue"
    | typeof MIC_INTERRUPTED
    | typeof MIC_RECONNECTING
    | typeof MIC_HELD_LABEL
    | typeof MIC_STUCK_LABEL;
  /** The mic is on for the session but held (see MicViewInput.held). */
  held?: "muted" | "other" | "reconnecting" | "stuck";
}

/** The mic's track is muted: the next tap fetches a fresh one. */
export const MIC_INTERRUPTED = "Mic interrupted · Tap to reconnect";
/** A fresh mic is on its way: taps leave it alone meanwhile. */
export const MIC_RECONNECTING = "Reconnecting the mic…";
/** A fresh track came back muted too. */
export const MIC_HELD_BY_OTHER = "Another app is using the mic";
/** ...and what the next tap does then: the orb is a voice toggle again. */
export const MIC_HELD_LABEL = `${MIC_HELD_BY_OTHER} · Tap to end voice` as const;
/** A reconnect that hasn't answered in time: no guess at why (it may just be slow to wake). */
export const MIC_STUCK_LABEL = "The mic isn't responding · Tap to end voice";

export function micView({ kit, micOn, paused = false, replyPaused = false, held = null }: MicViewInput): MicView {
  // A muted track is never "on", whatever the capture says.
  const mic = micOn && !held ? "on" : "off";
  const on = mic === "on";
  const h = held ? { held } : {};
  // A paused reply shows a pause sign whatever the mic does (the mic indicator still tells).
  if (kit === "speaking" && replyPaused) return { mic, orb: "paused", label: "Paused · tap to continue", ...h };
  if (kit === "thinking") return { mic, orb: on ? "thinking" : "idle", label: "Thinking", ...h };
  // The label says what a tap does: while a reply plays a tap pauses it, whatever the mic does,
  // so the held mic shows on the mic indicator only (micText) until the reply is done.
  if (kit === "speaking") return { mic, orb: on ? "speaking" : "idle", label: "Speaking", ...h };
  if (held === "muted") return { mic, orb: "idle", label: MIC_INTERRUPTED, held };
  if (held === "reconnecting") return { mic, orb: "idle", label: MIC_RECONNECTING, held };
  if (held === "other") return { mic, orb: "idle", label: MIC_HELD_LABEL, held };
  if (held === "stuck") return { mic, orb: "idle", label: MIC_STUCK_LABEL, held };
  if (on) return { mic, orb: "listening", label: "Listening" };
  return { mic, orb: "idle", label: paused ? "Paused" : "Mic off · Tap to talk" };
}

/** The mic indicator's text (also its accessible name): never colour alone. */
export function micText(view: MicView): string {
  if (view.held === "muted") return "Mic interrupted";
  if (view.held === "reconnecting") return "Mic reconnecting…";
  if (view.held === "other") return "Mic in use by another app";
  if (view.held === "stuck") return "Mic not responding";
  return view.mic === "on" ? "Mic on" : "Mic off";
}

/**
 * The orb button's `aria-pressed`: whether the mic is listening, from the capture itself. A tap
 * that only pauses or carries on a reply leaves it as it is; a held or stalled mic is not pressed.
 */
export function orbPressed(view: MicView): boolean {
  return view.mic === "on";
}
